import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

interface SplitTextProps {
  text: string;
  className?: string;
  delay?: number;
  duration?: number;
  ease?: string;
  splitType?: 'chars' | 'words' | 'lines';
  from?: any;
  to?: any;
  threshold?: number;
  rootMargin?: string;
  textAlign?: 'left' | 'center' | 'right';
  onLetterAnimationComplete?: () => void;
}

const SplitText: React.FC<SplitTextProps> = ({
  text,
  className = '',
  delay = 0,
  duration = 0.6,
  ease = 'easeOut',
  splitType = 'chars',
  from = { opacity: 0, y: 40 },
  to = { opacity: 1, y: 0 },
  threshold = 0.1,
  rootMargin = '-100px',
  textAlign = 'left',
  onLetterAnimationComplete
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [animatedCount, setAnimatedCount] = useState(0);

  // Observer pour détecter quand l'élément entre dans la vue
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [threshold, rootMargin]);

  // Diviser le texte selon le type
  const splitText = () => {
    switch (splitType) {
      case 'chars':
        return text.split('').map((char, index) => (
          <motion.span
            key={index}
            initial={from}
            animate={isInView ? to : from}
            transition={{
              duration,
              delay: delay + (index * 50) / 1000, // Convertir en secondes
              ease: ease as any
            }}
            onAnimationComplete={() => {
              setAnimatedCount(prev => {
                const newCount = prev + 1;
                if (newCount === text.length && onLetterAnimationComplete) {
                  onLetterAnimationComplete();
                }
                return newCount;
              });
            }}
            style={{ display: 'inline-block' }}
          >
            {char === ' ' ? '\u00A0' : char}
          </motion.span>
        ));
      
      case 'words':
        return text.split(' ').map((word, index) => (
          <motion.span
            key={index}
            initial={from}
            animate={isInView ? to : from}
            transition={{
              duration,
              delay: delay + (index * 100) / 1000,
              ease: ease as any
            }}
            style={{ display: 'inline-block', marginRight: '0.25em' }}
          >
            {word}
          </motion.span>
        ));
      
      case 'lines':
        return text.split('\n').map((line, index) => (
          <motion.div
            key={index}
            initial={from}
            animate={isInView ? to : from}
            transition={{
              duration,
              delay: delay + (index * 150) / 1000,
              ease: ease as any
            }}
            style={{ display: 'block' }}
          >
            {line}
          </motion.div>
        ));
      
      default:
        return text;
    }
  };

  const getTextAlignClass = () => {
    switch (textAlign) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  };

  return (
    <div
      ref={containerRef}
      className={`${className} ${getTextAlignClass()}`}
      style={{ overflow: 'hidden' }}
    >
      {splitText()}
    </div>
  );
};

export default SplitText;
